import React, { useState } from 'react';
import '@progress/kendo-theme-default/dist/all.css';
import '../theme.css';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { FormBuilderProvider, useFormBuilder, type FormBuilderCallbacks } from '../context/FormBuilderContext';
import type { FormDefinition, FormDocument, FormVersion } from '../types';
import type { FieldTypeDefinition } from '../fieldTypes/registry';
import { decodeDragId } from '../utils/dnd';
import { Toolbar } from './Toolbar';
import { FormDetailsBar } from './FormDetailsBar';
import { FieldPalette } from './FieldPalette';
import { Canvas } from './Canvas';
import { PropertyInspector } from './PropertyInspector';
import { PreviewDrawer } from './PreviewDrawer';
import { VersionPanel } from './VersionPanel';

function findSectionIdForField(document: FormDocument, fieldId: string): string | null {
  for (const s of document.sections) if (s.fields.some((f) => f.id === fieldId)) return s.id;
  return null;
}

function findFieldIndex(document: FormDocument, fieldId: string): number {
  for (const s of document.sections) {
    const idx = s.fields.findIndex((f) => f.id === fieldId);
    if (idx >= 0) return idx;
  }
  return 0;
}

function sectionFieldCount(document: FormDocument, sectionId: string): number {
  return document.sections.find((s) => s.id === sectionId)?.fields.length ?? 0;
}

function FormBuilderInner() {
  const { document, addField, moveField, reorderSections, previewOpen, togglePreview, importError, clearImportError } = useFormBuilder();
  const [showVersions, setShowVersions] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    const activeId = decodeDragId(String(active.id));
    const overId = decodeDragId(String(over.id));
    if (!activeId || !overId) return;

    if (activeId.kind === 'palette') {
      if (overId.kind === 'section-body') {
        addField(overId.sectionId, activeId.fieldType);
      } else if (overId.kind === 'field') {
        const sectionId = findSectionIdForField(document, overId.fieldId);
        if (sectionId) addField(sectionId, activeId.fieldType, findFieldIndex(document, overId.fieldId));
      }
      return;
    }

    if (activeId.kind === 'field') {
      if (overId.kind === 'section-body') {
        moveField(activeId.fieldId, overId.sectionId, sectionFieldCount(document, overId.sectionId));
      } else if (overId.kind === 'field' && activeId.fieldId !== overId.fieldId) {
        const sectionId = findSectionIdForField(document, overId.fieldId);
        if (sectionId) moveField(activeId.fieldId, sectionId, findFieldIndex(document, overId.fieldId));
      }
      return;
    }

    if (activeId.kind === 'section' && overId.kind === 'section' && activeId.sectionId !== overId.sectionId) {
      // Sections only reorder among siblings (same parentId) — dragging a section into a
      // different nesting branch is not supported; use "+ Subsection" to create nesting.
      const activeSection = document.sections.find((s) => s.id === activeId.sectionId);
      const overSection = document.sections.find((s) => s.id === overId.sectionId);
      if (!activeSection || !overSection || activeSection.parentId !== overSection.parentId) return;
      const siblingIds = document.sections
        .filter((s) => s.parentId === activeSection.parentId)
        .sort((a, b) => a.order - b.order)
        .map((s) => s.id);
      const oldIndex = siblingIds.indexOf(activeId.sectionId);
      const newIndex = siblingIds.indexOf(overId.sectionId);
      reorderSections(arrayMove(siblingIds, oldIndex, newIndex));
    }
  }

  return (
    <>
      <Toolbar onToggleVersions={() => setShowVersions(true)} />
      <FormDetailsBar />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div
            style={{
              width: 220,
              flexShrink: 0,
              borderRight: '1px solid var(--kendo-color-border)',
              overflowY: 'auto',
              background: 'var(--kendo-color-app-surface)',
            }}
          >
            <FieldPalette />
          </div>
          <Canvas />
          <div
            style={{
              width: 300,
              flexShrink: 0,
              borderLeft: '1px solid var(--kendo-color-border)',
              overflowY: 'auto',
              background: 'var(--kendo-color-app-surface)',
            }}
          >
            <PropertyInspector />
          </div>
        </div>
      </DndContext>
      {previewOpen && <PreviewDrawer onClose={togglePreview} />}
      {showVersions && <VersionPanel onClose={() => setShowVersions(false)} />}
      <NotificationGroup style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
        {importError && (
          <Notification type={{ style: 'error', icon: true }} closable onClose={clearImportError}>
            <span>{importError}</span>
          </Notification>
        )}
      </NotificationGroup>
    </>
  );
}

// ─── Public component ──────────────────────────────────────────────────────

export interface FormBuilderProps {
  formDefinition: FormDefinition;
  versions: FormVersion[];
  /** The version currently being edited. Must be a `draft` for editing to be enabled. */
  activeVersion: FormVersion;
  /** Extend the built-in field type registry — the modern replacement for adding a QuestionFormat row. */
  fieldTypes?: FieldTypeDefinition[];
  callbacks: FormBuilderCallbacks;
  /** Enable postMessage bridge for iframe embedding. */
  iframeMode?: boolean;
  readOnly?: boolean;
  /** Pass true immediately after creating a new form to enable Form Type selection (locked after initial save). */
  isNewForm?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export function FormBuilder({
  formDefinition,
  versions,
  activeVersion,
  fieldTypes,
  callbacks,
  iframeMode = false,
  readOnly = false,
  isNewForm = false,
  style,
  className,
}: FormBuilderProps) {
  return (
    <FormBuilderProvider
      formDefinition={formDefinition}
      versions={versions}
      activeVersion={activeVersion}
      fieldTypes={fieldTypes}
      callbacks={callbacks}
      iframeMode={iframeMode}
      readOnly={readOnly}
      isNewForm={isNewForm}
    >
      <div
        className={className}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          // The component must be visually self-contained regardless of what's behind it
          // in the host page — without an explicit background here, host page styles
          // (e.g. a dark app shell) show through the gaps between panels.
          background: 'var(--kendo-color-app-surface)',
          color: 'var(--kendo-color-on-app-surface)',
          ...style,
        }}
        data-fulcrum-form-builder
      >
        <FormBuilderInner />
      </div>
    </FormBuilderProvider>
  );
}

// ─── Iframe wrapper (for the host side) ────────────────────────────────────

export interface FormBuilderIframeProps {
  src: string;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
}

export function FormBuilderIframe({ src, style, className, title = 'Form Builder' }: FormBuilderIframeProps) {
  return (
    <iframe
      src={src}
      title={title}
      className={className}
      style={{ border: 'none', width: '100%', height: '100%', ...style }}
    />
  );
}
